import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChevronRight, ShieldCheck } from "lucide-react";

const WORDMARK = "https://customer-assets.emergentagent.com/job_020b4353-d8b1-41a2-9f55-e68e3d0ed71c/artifacts/fsgsgk7e_Asset%203.png";

const DEV_ROLES = [
  { email: "owner@interlinked.global", role: "owner", label: "Owner" },
  { email: "founder@interlinked.global", role: "founder", label: "Founder" },
  { email: "partner@interlinked.global", role: "operating_partner", label: "Operating partner" },
  { email: "advisor@interlinked.global", role: "advisor", label: "Advisor" },
];

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 35.6 44 30.2 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function Login() {
  const { user, startGoogleLogin, devLogin } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showDev, setShowDev] = useState(false);

  // If a session is already active (or becomes active after login), enter the cockpit.
  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleGoogle = async () => {
    setBusy(true);
    try {
      await startGoogleLogin();
    } catch (e) {
      toast.error("Could not start Google sign-in. Check the OAuth configuration.");
      setBusy(false);
    }
  };

  const handleDev = async (email, role, label) => {
    setBusy(true);
    try {
      await devLogin(email, role, label);
      navigate("/");
    } catch (e) {
      toast.error("Developer login failed.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left, identity */}
      <div className="relative hidden lg:flex flex-col justify-between border-r border-border bg-[#0B0D10] p-10">
        <img src={WORDMARK} alt="Interlinked Global" className="h-8 w-auto object-contain" />
        <div className="max-w-md">
          <p className="font-display text-3xl leading-tight text-foreground">
            The operating system for the studio.
          </p>
          <p className="mt-4 text-sm text-muted-foreground leading-6">
            Capital position, venture health, and what matters this week. One calm, dense view, read in about ten seconds.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3">
            {["Capital", "Ventures", "This week"].map((f) => (
              <div key={f} className="rounded-md border border-border bg-card px-3 py-3">
                <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground font-mono">Singapore and Perth</p>
      </div>

      {/* Right, sign-in */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <img src={WORDMARK} alt="Interlinked Global" className="h-7 w-auto object-contain" />
          </div>
          <h2 className="font-display text-2xl text-foreground">Interlinked OS</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to the cockpit.</p>

          <div className="mt-8 rounded-md border border-border bg-card p-5">
            <Button
              data-testid="google-signin-button"
              onClick={handleGoogle}
              disabled={busy}
              className="w-full h-11 bg-white text-[#111] hover:bg-white/90 font-medium gap-2"
            >
              <GoogleGlyph /> Continue with Google
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">Use your Interlinked Global Google account.</p>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <button
              data-testid="toggle-dev-login"
              onClick={() => setShowDev((s) => !s)}
              className="flex w-full items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" strokeWidth={1.75} /> Developer login</span>
              <ChevronRight className={`h-4 w-4 transition-transform ${showDev ? "rotate-90" : ""}`} strokeWidth={1.75} />
            </button>

            {showDev && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-muted-foreground">Temporary access for building and review. Pick a role to enter.</p>
                <div className="grid grid-cols-2 gap-2">
                  {DEV_ROLES.map((r) => (
                    <button
                      key={r.role}
                      data-testid={`dev-login-${r.role}`}
                      disabled={busy}
                      onClick={() => handleDev(r.email, r.role, r.label)}
                      className="rounded-md border border-border bg-background px-3 py-2 text-left text-sm text-foreground hover:border-[rgba(43,79,216,0.5)] hover:bg-white/5 transition-colors"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <p className="mt-6 text-xs text-muted-foreground leading-5">
            Access is role based. The cap table and financial settings are restricted to Owner and Founder.
          </p>
        </div>
      </div>
    </div>
  );
}
